import React from 'react';
import { Badge, STATUS_COLORS, STRING_DIGITS, noteColor } from '../../../types/fingeringEditor';
import { chordStatus } from '../../../utils/chordStatus';
import { TOUCH_DRAG_OFFSET_Y } from '../../../constants/dragOffset';

// issue #16 Phase 4: 範囲選択 (matters when multi-select active).
// - 'none': 選択外。 リング描画なし。
// - 'primary': 単一選択。 既存アニメ白リングを描画 (chord は先頭 note にのみ)。
// - 'multi': 複数選択メンバー。 静的 amber リングを描画 (アニメなし)。
export type SelectionState = 'none' | 'primary' | 'multi';

interface BadgeSvgProps {
  badge: Badge;
  chordNotes?: Badge[];
  isSelected: boolean;
  isDragging: boolean;
  // drag 中の SVG 座標オフセット。isDragging = true の時だけ設定される想定。
  // null の時は未 drag、BadgeSvg 側で transform="translate(0,0)" と同等扱い。
  dragDelta?: { dx: number; dy: number } | null;
  // F-C-Lite (issue #28): true のとき、 isDragging 中に Y 方向の指オフセット (-18px) と drop-shadow を適用。
  // Mobile で指の真上にバッジを浮かせる UX。 Desktop では false を渡す。 既定: false。
  applyTouchOffset?: boolean;
  // issue #16 Phase 4: 範囲選択 state。 省略時は isSelected から後方互換 derive ('primary' or 'none')。
  selectionState?: SelectionState;
  // issue #166: 繰り返しグループの対応バッジを「関わった瞬間だけ」一時ハイライトする。
  // 常時マーキングはしない (issue #120 の「譜面は静かに」方針)。amber 選択リングとは別色。
  isHighlighted?: boolean;
  onPointerDown: (id: number, e: React.PointerEvent) => void;
}

// issue #16 Phase 4: badgesInRect utility 等で共有するため export 化 (旧名 SINGLE_R)。
export const BADGE_SINGLE_R = 4.8;
const SINGLE_R = BADGE_SINGLE_R;
const SINGLE_FRET_SIZE = 9.0;
const SINGLE_DIGIT_SIZE = 7.2;
const SINGLE_GAP = 1.5;

// issue #16 Phase 4: multi select の amber ring 色 (spec §6.6)。
const MULTI_RING_STROKE = '#f59e0b';

// issue #166: 繰り返しグループの一時ハイライトリング色 (cyan)。
// amber 選択リング (#f59e0b) や弦色・DEFAULT_NOTE_COLOR いずれとも別色にして役割を区別する。
const HIGHLIGHT_RING_STROKE = '#06b6d4';
const HIGHLIGHT_RING_WIDTH = 1.4;

const CHORD_R = 4.2;
const CHORD_DIGIT_SIZE = 6.4;
const CHORD_FRET_SIZE = 8.0;
const CHORD_NOTE_GAP = 6.0;
const CHORD_X_OFFSET = 4.0;
const CHORD_LINE_H = CHORD_R * 2 + 1.0;

// H4 描画仕様 (色枠+色字、 白半透明 fill = 中間案 V-3)。
// V-1 (0.85) → V-2 (完全透明) は実機検証 NG (タップ視認性 / 形状認知が弱い)、 V-3 (0.3) で着地。
// PDF (annotate_pdf.py) は完全透明 (枠線のみ) のまま、 エディタは中間案で仕様分岐 (issue #15)。
const BADGE_FILL_OPACITY_FE = 0.3;
const BADGE_STROKE_WIDTH = 1.2;
const CHORD_STROKE_WIDTH = 1.0;
const STATUS_OVERLAY_OPACITY = 0.3;

// F-C-Lite (issue #28): Mobile drag 中の視覚 Y オフセットは constants/dragOffset.ts に集約。
// P-2 採用 (issue #28 v3) により Core.finalizeDrag でも同 const を参照して「視覚位置にドロップ」 を実現。
// drag 中の浮き上がり感を演出する drop-shadow filter (Mobile drag 時のみ適用)。
// オフセット (0, 2) + ぼかし 4px + 半透明黒で「バッジが指の上に浮いている」視覚効果を作る。
const TOUCH_DRAG_FILTER = 'drop-shadow(0 2px 4px rgba(0,0,0,0.35))';

// drag 中の点線枠 rect (黄色 / dasharray="3 2") を描画するヘルパ。
// spec §4.2 F-2: 単音 / 和音の両方で形を統一する invariant 表現。
function renderDashedOutline(props: { x: number; y: number; width: number; height: number }) {
  return (
    <rect data-testid="badge-dashed-outline" x={props.x} y={props.y} width={props.width} height={props.height}
      rx={3} fill="none" stroke="#fbbf24" strokeWidth={1.2} strokeDasharray="3 2" />
  );
}

const BadgeSvg: React.FC<BadgeSvgProps> = ({
  badge,
  chordNotes,
  isSelected,
  isDragging,
  dragDelta,
  applyTouchOffset = false,
  selectionState,
  isHighlighted = false,
  onPointerDown,
}) => {
  const isChord = chordNotes && chordNotes.length > 1;
  // issue #16 Phase 4: 後方互換 derive。 selectionState 指定があればそれを優先、 無ければ isSelected から導出。
  const effectiveState: SelectionState =
    selectionState ?? (isSelected ? 'primary' : 'none');
  // drag 中は SVG transform で視覚 offset を表現 (H4/M6)。
  // finalize 時に badge.x/y に commit されるため、ここでの加算は視覚目的のみ。
  // F-C-Lite (issue #28): applyTouchOffset=true なら Y 方向に -TOUCH_DRAG_OFFSET_Y を加算
  // して指の真上にバッジが見えるよう浮かせる。
  const offsetY = applyTouchOffset ? TOUCH_DRAG_OFFSET_Y : 0;
  const transform = isDragging && dragDelta
    ? `translate(${dragDelta.dx} ${dragDelta.dy - offsetY})`
    : undefined;

  if (isChord) {
    if (chordNotes[0].id !== badge.id) return null;

    const baseX = badge.x;
    const baseY = badge.y - (chordNotes.length - 1) * CHORD_LINE_H / 2;
    const hitW = CHORD_R * 2 + CHORD_NOTE_GAP + 16;
    const hitH = chordNotes.length * CHORD_LINE_H + 4;
    const statusBg = STATUS_COLORS[chordStatus(chordNotes)]?.bg ?? STATUS_COLORS.auto.bg;
    // issue #50 確定: multi 選択中は status 不問でオレンジ背景、 primary/none は status 色のまま。
    const overlayFill = effectiveState === 'multi' ? MULTI_RING_STROKE : statusBg;

    return (
      <g data-testid="editor-badge" transform={transform} style={{
        cursor: isDragging ? 'grabbing' : 'pointer',
        opacity: isDragging ? 0.7 : 1,
        touchAction: 'none',
        ...(isDragging && applyTouchOffset ? { filter: TOUCH_DRAG_FILTER } : {}),
      }}
         onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); onPointerDown(badge.id, e); }}>
        <rect data-testid="badge-hit-area" x={baseX + CHORD_X_OFFSET - hitW / 2} y={baseY - 2} width={hitW} height={hitH} fill="transparent" />
        <rect data-testid="badge-status-overlay" x={baseX + CHORD_X_OFFSET - hitW / 2 + 2} y={baseY - 1}
          width={hitW - 4} height={hitH - 2} rx={2} fill={overlayFill} opacity={STATUS_OVERLAY_OPACITY} />
        {isDragging && renderDashedOutline({
          x: baseX + CHORD_X_OFFSET - hitW / 2 + 1,
          y: baseY - 2,
          width: hitW - 2,
          height: hitH,
        })}
        {isHighlighted && (
          <rect data-testid="badge-highlight-ring" x={baseX + CHORD_X_OFFSET - hitW / 2} y={baseY - 2}
            width={hitW} height={hitH} rx={3} fill="none"
            stroke={HIGHLIGHT_RING_STROKE} strokeWidth={HIGHLIGHT_RING_WIDTH}
            strokeDasharray="2 1.5" opacity={0.9} />
        )}
        {chordNotes.map((note, i) => {
          const color = noteColor(note.string);
          const cx = baseX + CHORD_X_OFFSET - CHORD_NOTE_GAP / 2 - CHORD_R;
          const cy = baseY + i * CHORD_LINE_H + CHORD_R;
          const fretX = baseX + CHORD_X_OFFSET + CHORD_NOTE_GAP / 2;
          // issue #50 確定: chord 全体が primary のときに先頭 note にだけオレンジ静的リングを集約。
          // C-2 ポリシーで chord = beat 単位選択 (個別 note は選ばない) のため、 isTarget は単純化可。
          // multi は背景オレンジで表現しリングは描かない。
          const isPrimaryTarget = effectiveState === 'primary' && note.id === badge.id;
          return (
            <g key={note.id}>
              {isPrimaryTarget && (
                <circle data-testid="badge-selection-ring" cx={cx} cy={cy} r={CHORD_R + 2} fill="none"
                  stroke={MULTI_RING_STROKE} strokeWidth={1.2} opacity={0.85} />
              )}
              {/* V-3 (issue #15): 色枠 (弦色 stroke) + 白半透明 fill (0.3) でタップ視認性確保。 PDF (annotate_pdf.py) は完全透明で仕様分岐。 */}
              <circle data-testid="badge-body-circle" cx={cx} cy={cy} r={CHORD_R}
                fill="#fff" fillOpacity={BADGE_FILL_OPACITY_FE}
                stroke={color} strokeWidth={CHORD_STROKE_WIDTH} />
              {note.string != null ? (
                <>
                  <text data-testid="badge-text" x={cx} y={cy} textAnchor="middle" dominantBaseline="central"
                    fontSize={CHORD_DIGIT_SIZE} fontWeight={700} fill={color} fontFamily="Helvetica, Arial, sans-serif"
                    style={{ pointerEvents: 'none', userSelect: 'none' }}>
                    {STRING_DIGITS[note.string! - 1]}
                  </text>
                  <text data-testid="badge-text" x={fretX} y={cy} textAnchor="start" dominantBaseline="central"
                    fontSize={CHORD_FRET_SIZE} fontWeight={700} fill={color} fontFamily="Helvetica, Arial, sans-serif"
                    style={{ pointerEvents: 'none', userSelect: 'none' }}>
                    {note.fret ?? '?'}
                  </text>
                </>
              ) : (
                <text data-testid="badge-text" x={cx} y={cy} textAnchor="middle" dominantBaseline="central"
                  fontSize={CHORD_DIGIT_SIZE} fontWeight={700} fill={color} fontFamily="Helvetica, Arial, sans-serif"
                  style={{ pointerEvents: 'none', userSelect: 'none' }}>
                  ?
                </text>
              )}
            </g>
          );
        })}
      </g>
    );
  }

  // 単音
  const color = noteColor(badge.string);
  const hasData = badge.string != null;
  const statusBg = STATUS_COLORS[badge.status]?.bg ?? STATUS_COLORS.auto.bg;
  // issue #50 確定: multi 選択中は status 不問でオレンジ背景、 primary/none は status 色のまま。
  const overlayFill = effectiveState === 'multi' ? MULTI_RING_STROKE : statusBg;

  return (
    <g data-testid="editor-badge" transform={transform} style={{
      cursor: isDragging ? 'grabbing' : 'pointer',
      opacity: isDragging ? 0.7 : 1,
      touchAction: 'none',
      ...(isDragging && applyTouchOffset ? { filter: TOUCH_DRAG_FILTER } : {}),
    }}
       onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); onPointerDown(badge.id, e); }}>
      <rect data-testid="badge-hit-area" x={badge.x - SINGLE_R - 2} y={badge.y - SINGLE_R - 2}
        width={SINGLE_R * 2 + 4} height={SINGLE_R * 2 + SINGLE_GAP + SINGLE_FRET_SIZE + 4} fill="transparent" />
      <rect data-testid="badge-status-overlay" x={badge.x - SINGLE_R - 1} y={badge.y - SINGLE_R - 1}
        width={SINGLE_R * 2 + 2} height={SINGLE_R * 2 + SINGLE_GAP + SINGLE_FRET_SIZE + 2}
        rx={2} fill={overlayFill} opacity={STATUS_OVERLAY_OPACITY} />
      {isDragging && renderDashedOutline({
        x: badge.x - SINGLE_R - 2,
        y: badge.y - SINGLE_R - 2,
        width: SINGLE_R * 2 + 4,
        height: SINGLE_R * 2 + SINGLE_GAP + SINGLE_FRET_SIZE + 4,
      })}
      {/* issue #50 確定: primary = 静的オレンジリング / multi = リングなし (背景オレンジで表現) / none = 何もなし */}
      {effectiveState === 'primary' && (
        <circle data-testid="badge-selection-ring" cx={badge.x} cy={badge.y} r={SINGLE_R + 2} fill="none"
          stroke={MULTI_RING_STROKE} strokeWidth={1.2} opacity={0.85} />
      )}
      {isHighlighted && (
        <circle data-testid="badge-highlight-ring" cx={badge.x} cy={badge.y} r={SINGLE_R + 3} fill="none"
          stroke={HIGHLIGHT_RING_STROKE} strokeWidth={HIGHLIGHT_RING_WIDTH}
          strokeDasharray="2 1.5" opacity={0.9} />
      )}
      {/* V-3 (issue #15): 色枠 (弦色 stroke) + 白半透明 fill (0.3) でタップ視認性確保。 PDF (annotate_pdf.py) は完全透明で仕様分岐。 */}
      <circle data-testid="badge-body-circle" cx={badge.x} cy={badge.y} r={SINGLE_R}
        fill="#fff" fillOpacity={BADGE_FILL_OPACITY_FE}
        stroke={color} strokeWidth={BADGE_STROKE_WIDTH} />
      {hasData ? (
        <>
          <text data-testid="badge-text" x={badge.x} y={badge.y} textAnchor="middle" dominantBaseline="central"
            fontSize={SINGLE_DIGIT_SIZE} fontWeight={700} fill={color} fontFamily="Helvetica, Arial, sans-serif"
            style={{ pointerEvents: 'none', userSelect: 'none' }}>
            {STRING_DIGITS[badge.string! - 1]}
          </text>
          <text data-testid="badge-text" x={badge.x} y={badge.y + SINGLE_R + SINGLE_GAP + SINGLE_FRET_SIZE * 0.35} textAnchor="middle" dominantBaseline="central"
            fontSize={SINGLE_FRET_SIZE} fontWeight={700} fill={color} fontFamily="Helvetica, Arial, sans-serif"
            style={{ pointerEvents: 'none', userSelect: 'none' }}>
            {badge.fret ?? '?'}
          </text>
        </>
      ) : (
        <text data-testid="badge-text" x={badge.x} y={badge.y} textAnchor="middle" dominantBaseline="central"
          fontSize={SINGLE_DIGIT_SIZE} fontWeight={700} fill={color} fontFamily="Helvetica, Arial, sans-serif"
          style={{ pointerEvents: 'none', userSelect: 'none' }}>
          ?
        </text>
      )}
    </g>
  );
};

export default React.memo(BadgeSvg);
